import { Injectable } from '@angular/core';
import { ZUORA_CART } from '@app/constants';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ProductZuoraCartItem, ZuoraCartModel } from './payment.models';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  count = new BehaviorSubject<number>(0);
  count$ = this.count.asObservable();
  hasProductAutoRenew = new BehaviorSubject<Boolean>(false);
  hasProductAutoRenew$ = this.hasProductAutoRenew.asObservable();
  private paymentSuccess = new BehaviorSubject<boolean>(false);

  constructor(
    private cookieService: CookieService,
  ) { }

  public countItemInCart() {
    const zuoraCart = this.getZuoraCart();
    if (zuoraCart && zuoraCart.products) {
      this.count.next(zuoraCart.products.length)

      if (zuoraCart.products.find(x => x.autoRenew)) {
        this.hasProductAutoRenew.next(true);
      }
      else {
        this.hasProductAutoRenew.next(false);
      }
    } else {
      this.count.next(0);
      this.hasProductAutoRenew.next(false);
    }
  }

  private getZuoraUserId() {
    return this.cookieService.get(ZUORA_CART.USER_ID);
  }

  public setZuoraUserId(userId: string) {
    this.cookieService.set(ZUORA_CART.USER_ID, userId, 365, '/');
    if (!this.getZuoraCart()) {
      this.setZuoraCart({
        userId,
        products: []
      });
    }
    this.countItemInCart();
  }

  public getZuoraCart(): ZuoraCartModel {
    const value = this.cookieService.get(ZUORA_CART.LIST_PRODUCT);
    const zuoraProductCart: ZuoraCartModel = value === '' ? null : JSON.parse(value);
    if(!zuoraProductCart || zuoraProductCart.userId != this.getZuoraUserId()){
      return null;
    }
    return zuoraProductCart;
  }

  private setZuoraCart(cart: ZuoraCartModel) {
    this.cookieService.set(ZUORA_CART.LIST_PRODUCT, JSON.stringify(cart), 365, '/');
  }

  public setProductToZuoraCart(product: ProductZuoraCartItem) {
    const userId = this.getZuoraUserId()
    var zuoraCart = this.getZuoraCart();
    
    //cookie hasn't been set yet
    if(!zuoraCart){
      this.setZuoraCart(<ZuoraCartModel>({
        userId,
        products: [product]
      }));
      return;
    } else{    
      
      if (!zuoraCart.products || zuoraCart.products.length == 0) {
        zuoraCart.products.push(product);
        this.setZuoraCart(zuoraCart);
      } else {
        //only add product if it doesn't already exist
        if(!zuoraCart.products.some(p => p.chartEntitlementId == product.chartEntitlementId)){
          zuoraCart.products.push(product);
          this.setZuoraCart(zuoraCart);
        }
      }
    }
    this.countItemInCart();
  }

  public updateProductInZuoraCart(chartEntitlementId: string, autoRenew: boolean) {
    try {
      const zuoraCart = this.getZuoraCart();
      const productIndex = zuoraCart.products.findIndex(existingProduct => existingProduct.chartEntitlementId == chartEntitlementId);
      if(productIndex != -1){
        zuoraCart.products[productIndex].autoRenew = autoRenew;
        this.setZuoraCart(zuoraCart);
      }
    } catch (ex) {}
    this.countItemInCart();
  }

  public removeProductFromZuoraCart(chartEntitlementId: string) {
    try {
      var zuoraCart = this.getZuoraCart()

      if (zuoraCart?.products) {
        zuoraCart.products = zuoraCart.products.filter(item => item.chartEntitlementId !== chartEntitlementId);
        this.setZuoraCart(zuoraCart);
      }
      this.countItemInCart();
    } catch (ex) { }
  }

  public resetZuoraCart() {
    const zuoraCart = this.getZuoraCart()
    if (zuoraCart?.userId) {
      this.setZuoraCart(<ZuoraCartModel>({
        userId: zuoraCart.userId,
        products: []
      }))
    }
    this.countItemInCart();
  }

  public sendPaymentSuccessStatus(status: any) {
    this.paymentSuccess.next(status);
  }

  public getPaymentSuccessStatus(): Observable<any> {
    return this.paymentSuccess.asObservable()
  }

  public turnOffAutoRenewAllProductInZuoraCart() {
    try {
      const zuoraCart = this.getZuoraCart();
      zuoraCart.products.forEach(product => {
        product.autoRenew = false;
      });
      this.setZuoraCart(zuoraCart);
      this.countItemInCart();
    } catch (ex) { console.log(ex); }
  }

  public getZuoraCartEarlyRenewal(): ZuoraCartModel {
    const value = this.cookieService.get(ZUORA_CART.LIST_PRODUCT_EARLY_RENEWAL);
    const zuoraProductCart: ZuoraCartModel = value === '' ? null : JSON.parse(value);
    if (!zuoraProductCart || zuoraProductCart.userId != this.getZuoraUserId() || zuoraProductCart.products.length == 0) {
      return null;
    }
    return zuoraProductCart;
  }

  private setZuoraCartEarlyRenewal(cart: ZuoraCartModel) {
    this.cookieService.set(ZUORA_CART.LIST_PRODUCT_EARLY_RENEWAL, JSON.stringify(cart), 365, '/');
  }

  public setProductToZuoraCartEarlyRenewal(product: ProductZuoraCartItem) {
    const userId = this.getZuoraUserId()
    var zuoraCart = this.getZuoraCartEarlyRenewal();

    //cookie hasn't been set yet
    if (!zuoraCart) {
      this.setZuoraCartEarlyRenewal(<ZuoraCartModel>({
        userId,
        products: [product]
      }));
      return;
    } else {

      if (!zuoraCart.products || zuoraCart.products.length == 0) {
        zuoraCart.products.push(product);
        this.setZuoraCartEarlyRenewal(zuoraCart);
      } else {
        //only add product if it doesn't already exist
        if (!zuoraCart.products.some(p => p.chartEntitlementId == product.chartEntitlementId)) {
          zuoraCart.products.push(product);
          this.setZuoraCartEarlyRenewal(zuoraCart);
        }
      }
    }
  }

  public resetZuoraCartEarlyRenewal() {
    const zuoraCart = this.getZuoraCartEarlyRenewal()
    if (zuoraCart?.userId) {
      this.setZuoraCartEarlyRenewal(<ZuoraCartModel>({
        userId: zuoraCart.userId,
        products: []
      }))
    }
  }

  public updateProductInZuoraCartEarlyRenewal(chartEntitlementId: string, autoRenew: boolean) {
    try {
      const zuoraCart = this.getZuoraCartEarlyRenewal();
      const productIndex = zuoraCart.products.findIndex(existingProduct => existingProduct.chartEntitlementId == chartEntitlementId);
      if (productIndex != -1) {
        zuoraCart.products[productIndex].autoRenew = autoRenew;
        this.setZuoraCartEarlyRenewal(zuoraCart);
      }
    } catch (ex) {
      console.log(ex);
    }
  }
}
